import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import { useT } from '../../i18n/LocaleContext';
import { formatDateInput } from '../../utils/format';

const STATUSES = ['open', 'closed', 'completed', 'cancelled'];

const emptyForm = {
  name: '',
  destination: '',
  date: '',
  capacity: '',
  registered: 0,
  price: '',
  status: 'open',
  description: '',
};

export default function EventFormModal({ isOpen, onClose, onSubmit, initial }) {
  const t = useT();
  const isEdit = Boolean(initial);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initial) {
      setForm({
        name: initial.name || '',
        destination: initial.destination || '',
        date: formatDateInput(initial.date),
        capacity: initial.capacity ?? '',
        registered: initial.registered ?? 0,
        price: initial.price ?? '',
        status: initial.status || 'open',
        description: initial.description || '',
      });
    } else {
      setForm({ ...emptyForm });
    }
    setErrors({});
  }, [isOpen, initial]);

  const statusOptions = useMemo(
    () => STATUSES.map((s) => ({ value: s, label: t(`events.statuses.${s}`) })),
    [t]
  );

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('validation.required');
    if (!form.destination.trim()) next.destination = t('validation.required');
    if (!form.date) next.date = t('validation.required');
    const cap = Number(form.capacity || 0);
    if (Number.isNaN(cap) || cap < 0) next.capacity = t('validation.number_required');
    const price = Number(form.price);
    if (form.price === '' || Number.isNaN(price) || price < 0) next.price = t('validation.number_required');
    const reg = Number(form.registered);
    if (Number.isNaN(reg) || reg < 0) next.registered = t('validation.number_required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        ...form,
        capacity: Number(form.capacity || 0),
        registered: Number(form.registered),
        price: Number(form.price),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={isEdit ? t('events.edit_event') : t('events.new_event')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting}>
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <FormField label={t('events.field_name')} htmlFor="name" required error={errors.name}>
          <Input id="name" value={form.name} onChange={set('name')} invalid={Boolean(errors.name)} autoFocus />
        </FormField>
        <div className="form-row form-row--2">
          <FormField label={t('events.field_destination')} htmlFor="destination" required error={errors.destination}>
            <Input id="destination" value={form.destination} onChange={set('destination')} invalid={Boolean(errors.destination)} />
          </FormField>
          <FormField label={t('events.field_date')} htmlFor="date" required error={errors.date}>
            <Input id="date" type="date" value={form.date} onChange={set('date')} invalid={Boolean(errors.date)} />
          </FormField>
        </div>
        <div className="form-row form-row--3">
          <FormField label={t('events.field_capacity')} htmlFor="capacity" error={errors.capacity} hint={t('events.capacity_hint')}>
            <Input id="capacity" type="number" min="0" value={form.capacity} onChange={set('capacity')} invalid={Boolean(errors.capacity)} />
          </FormField>
          <FormField label={t('events.field_registered')} htmlFor="registered" error={errors.registered}>
            <Input id="registered" type="number" min="0" value={form.registered} onChange={set('registered')} invalid={Boolean(errors.registered)} />
          </FormField>
          <FormField label={t('events.field_price')} htmlFor="price" required error={errors.price}>
            <Input id="price" type="number" min="0" value={form.price} onChange={set('price')} invalid={Boolean(errors.price)} />
          </FormField>
        </div>
        <FormField label={t('events.field_status')} htmlFor="status">
          <Select id="status" options={statusOptions} value={form.status} onChange={set('status')} />
        </FormField>
        <FormField label={t('events.field_description')} htmlFor="description" hint={t('common.optional')}>
          <Textarea id="description" value={form.description} onChange={set('description')} rows={4} />
        </FormField>
      </form>
    </Modal>
  );
}
